<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import { normalizeSimilarListingsResponse } from 'dashboard/helper/lais/imovelPresenter';

const props = defineProps({
  imovelId: { type: [String, Number], default: '' },
  accountId: { type: [String, Number], default: '' },
  kind: { type: String, default: 'neighbourhood' },
  limit: { type: Number, default: 10 },
  title: { type: String, default: 'Outros imóveis na vizinhança' },
});

const emit = defineEmits(['loaded', 'cardClick']);

const isLoading = ref(false);
const error = ref('');
const items = ref([]);
const currentIndex = ref(0);
const recommendationId = ref('');
const recommendationType = ref('');
const touchStartX = ref(null);

const currentItem = computed(() => items.value[currentIndex.value] || null);
const counterText = computed(
  () => `${currentIndex.value + 1}/${items.value.length}`
);

const formatBRL = value =>
  value == null
    ? 'Valor sob consulta'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(value);

const goPrev = () => {
  if (!items.value.length) return;
  currentIndex.value = Math.max(0, currentIndex.value - 1);
};

const goNext = () => {
  if (!items.value.length) return;
  currentIndex.value = Math.min(items.value.length - 1, currentIndex.value + 1);
};

const handleTouchStart = event => {
  touchStartX.value = event.touches?.[0]?.clientX ?? null;
};

const handleTouchEnd = event => {
  if (touchStartX.value == null) return;
  const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.value;
  const delta = endX - touchStartX.value;
  touchStartX.value = null;
  if (Math.abs(delta) < 36) return;
  if (delta > 0) {
    goPrev();
  } else {
    goNext();
  }
};

const handleCardClick = () => {
  if (currentItem.value?.id && recommendationId.value) {
    ImovelAPI.trackRecommendationClick(currentItem.value.id, {
      recommendation_id: recommendationId.value,
    }).catch(() => {});
  }

  emit('cardClick', {
    item: currentItem.value,
    recommendationId: recommendationId.value,
    recommendationType: recommendationType.value,
  });
};

const load = async () => {
  if (!props.imovelId || !props.accountId) return;
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await ImovelAPI.similar(props.imovelId, {
      kind: props.kind,
      limit: props.limit,
    });
    const normalized = normalizeSimilarListingsResponse(data, {
      accountId: props.accountId,
    });
    items.value = normalized.items;
    recommendationId.value = normalized.recommendationId;
    recommendationType.value = normalized.recommendationType;
    currentIndex.value = 0;
    emit('loaded', normalized);
  } catch (e) {
    error.value = 'Não foi possível carregar imóveis semelhantes.';
    items.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(() => [props.imovelId, props.accountId, props.kind, props.limit], load, {
  immediate: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="grid gap-3">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-[16px] font-semibold text-lais-text">
        {{ title }}
      </h3>
      <div v-if="items.length > 1" class="flex items-center gap-1">
        <span class="mr-1 text-[12px] font-semibold text-lais-text-muted">
          {{ counterText }}
        </span>
        <button
          type="button"
          class="grid size-8 place-items-center rounded-full border border-lais-border bg-white text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
          aria-label="Imóvel anterior"
          @click="goPrev"
        >
          <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="grid size-8 place-items-center rounded-full border border-lais-border bg-white text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
          aria-label="Próximo imóvel"
          @click="goNext"
        >
          <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="grid min-h-[180px] place-items-center rounded-lais border border-lais-border bg-white text-[13px] text-lais-text-muted"
    >
      Carregando similares…
    </div>
    <div
      v-else-if="error"
      class="rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800"
    >
      {{ error }}
    </div>
    <RouterLink
      v-else-if="currentItem"
      :to="currentItem.to"
      class="grid overflow-hidden rounded-lais border border-lais-border bg-white transition-colors hover:border-lais-primary"
      @click="handleCardClick"
      @touchstart.passive="handleTouchStart"
      @touchend.passive="handleTouchEnd"
    >
      <div class="grid gap-0 md:grid-cols-[220px_minmax(0,1fr)]">
        <div class="aspect-[16/10] bg-lais-bg-soft md:aspect-auto">
          <img
            v-if="currentItem.thumbnail"
            :src="currentItem.thumbnail"
            :alt="currentItem.title"
            class="h-full w-full object-cover"
          />
          <div v-else class="grid h-full min-h-[150px] place-items-center">
            <span
              class="i-woot-lais-amenity-building size-12 text-lais-text-muted"
              aria-hidden="true"
            />
          </div>
        </div>
        <div class="grid gap-3 p-4">
          <div>
            <p class="text-[15px] font-semibold text-lais-text">
              {{ currentItem.title }}
            </p>
            <p
              v-if="currentItem.subtitle"
              class="mt-1 text-[13px] text-lais-text-muted"
            >
              {{ currentItem.subtitle }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2 text-[12px]">
            <span
              v-for="detail in currentItem.details"
              :key="detail"
              class="rounded-full bg-lais-bg-soft px-2.5 py-1 font-semibold text-lais-text-body"
            >
              {{ detail }}
            </span>
            <span
              v-if="currentItem.distanceLabel"
              class="rounded-full bg-lais-primary-50 px-2.5 py-1 font-semibold text-lais-primary"
            >
              {{ currentItem.distanceLabel }}
            </span>
          </div>
          <div class="flex items-center justify-between gap-3">
            <p class="text-[18px] font-semibold text-lais-primary">
              {{ formatBRL(currentItem.price) }}
            </p>
            <span class="text-[12px] font-semibold text-lais-link">
              Ver anúncio
            </span>
          </div>
        </div>
      </div>
    </RouterLink>
  </section>
</template>
